export type ContentKind = "journal" | "guide" | "script";

export interface ContentItem {
  slug: string;
  kind: ContentKind;
  title: string;
  description: string;
  date: string;
  tags: string[];
  body: string;
}

export interface LinkItem {
  name: string;
  url: string;
  description: string;
  category?: string;
}

export interface LinkSection {
  title: string;
  items: LinkItem[];
}

export interface SiteAuthor {
  name: string;
  location: string;
  roles: string;
  stack: string;
  fuel: string;
  bio: string;
  image: string;
}

export interface BackgroundBlob {
  colorStart: string;
  colorEnd: string;
  /** 0..1 — relative size vs the container's smallest side. */
  size: number;
  /** Initial top/left as % of the container. */
  top: number;
  left: number;
  /** Blur radius in px. */
  blur: number;
  /** Opacity 0..1. */
  opacity: number;
  /** Seconds for one morph + rotate cycle. */
  duration: number;
  /** Start delay in seconds (negative = pre-rolled). */
  delay: number;
  /** Rotation direction. */
  reverse: boolean;
}

export interface BackgroundConfig {
  /** Grain overlay that kills color banding. */
  grain: boolean;
  /** Overall opacity of the background layer, 0..1. */
  containerOpacity: number;
  blobs: BackgroundBlob[];
}

export const DEFAULT_BACKGROUND: BackgroundConfig = {
  grain: true,
  containerOpacity: 0.7,
  blobs: [
    { colorStart: "#cd1c18", colorEnd: "#ff8a73", size: 0.85, top: 10, left: 8, blur: 80, opacity: 0.45, duration: 28, delay: 0, reverse: false },
    { colorStart: "#0c4a6e", colorEnd: "#0ea5e9", size: 0.7, top: 35, left: 55, blur: 90, opacity: 0.42, duration: 36, delay: -8, reverse: true },
    { colorStart: "#f59e0b", colorEnd: "#fde68a", size: 0.55, top: 60, left: 20, blur: 70, opacity: 0.32, duration: 32, delay: -14, reverse: false },
    { colorStart: "#10b981", colorEnd: "#34d399", size: 0.45, top: 5, left: 65, blur: 60, opacity: 0.38, duration: 24, delay: -4, reverse: true },
  ],
};

export interface SiteMeta {
  name: string;
  tagline: string;
  url: string;
  email: string;
  remoteSupportUrl: string;
  remoteSupportLabel: string;
  author: SiteAuthor;
  background?: BackgroundConfig;
}

/** Current footer remote-support link defaults. These are the values a fresh
 * deploy ships with and the fallback the footer renders when content omits
 * them. */
export const DEFAULT_REMOTE_SUPPORT_LABEL = "Remote Support Admin";
export const DEFAULT_REMOTE_SUPPORT_URL = "https://app.level.io/";

/** Pre-Level.io footer link that may still be sitting in a saved Cloudflare KV
 * content copy. The one-way migration below rewrites these to the defaults. */
export const LEGACY_REMOTE_SUPPORT_LABEL = "Family Remote Support";
export const LEGACY_REMOTE_SUPPORT_URL = "https://wilsonfamily.screenconnect.com";

/**
 * Narrowly corrects a stored footer remote-support link in place: if the URL is
 * still the legacy ScreenConnect URL it becomes {@link DEFAULT_REMOTE_SUPPORT_URL},
 * and if the label is missing/empty or still the legacy label it becomes
 * {@link DEFAULT_REMOTE_SUPPORT_LABEL}. A URL that is already something else is
 * left untouched. Returns true when any field was changed so callers can persist
 * the correction without wiping the rest of the saved content.
 */
export function migrateRemoteSupportLink(site: {
  remoteSupportUrl?: unknown;
  remoteSupportLabel?: unknown;
}): boolean {
  let changed = false;

  if (site.remoteSupportUrl === LEGACY_REMOTE_SUPPORT_URL) {
    site.remoteSupportUrl = DEFAULT_REMOTE_SUPPORT_URL;
    changed = true;
  }

  const label = site.remoteSupportLabel;
  const labelMissing = typeof label !== "string" || label.trim() === "";
  if ((labelMissing || label === LEGACY_REMOTE_SUPPORT_LABEL) && label !== DEFAULT_REMOTE_SUPPORT_LABEL) {
    site.remoteSupportLabel = DEFAULT_REMOTE_SUPPORT_LABEL;
    changed = true;
  }

  return changed;
}

export interface NavItem {
  href: string;
  label: string;
}

export interface SiteContentData {
  site: SiteMeta;
  navItems: NavItem[];
  content: ContentItem[];
  gearSections: LinkSection[];
  appSections: LinkSection[];
  serviceSections: LinkSection[];
}

export function getContentByKind(content: ContentItem[], kind: ContentKind): ContentItem[] {
  return content
    .filter((item) => item.kind === kind)
    .sort((a, b) => b.date.localeCompare(a.date));
}

export function getContentBySlug(
  content: ContentItem[],
  kind: ContentKind,
  slug: string,
): ContentItem | undefined {
  return content.find((item) => item.kind === kind && item.slug === slug);
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function contentPath(item: ContentItem): string {
  const segment = item.kind === "journal" ? "journal" : `${item.kind}s`;
  return `/${segment}/${item.slug}`;
}

/** Extracts the source of the first fenced code block, stripping the
 * opening language tag (```powershell) — used for the raw .ps1 download
 * and the `iwr | iex` one-liner on script pages. Falls back to the whole
 * body if no fence is found. */
export function getRawScriptSource(body: string): string {
  const match = body.match(/```[^\n]*\n([\s\S]*?)```/);
  const source = match ? match[1] : body;
  return source.replace(/\n$/, "");
}

export function rawScriptPath(item: ContentItem): string {
  return `/scripts/${item.slug}/raw.ps1`;
}
